import type { PrescriptionCard } from '../api/performance.api';
import { getLineBadge, isBelowMinimumEstimate } from '../lib/performance';
import { DrugNameLink } from '@/shared/components/drug-drawer/DrugNameLink';
import { Badge } from '@/shared/components/ui/badge';
import { cn } from '@/shared/lib/utils';
import { formatYm } from '@/shared/utils/format';
import type { ReactNode } from 'react';

/** 값이 없으면 Figma처럼 "-" */
const num = (v: number | null | undefined) =>
  v === null || v === undefined ? '-' : Math.floor(v).toLocaleString('ko-KR');
const won = (v: number | null | undefined) =>
  v === null || v === undefined ? '-' : `${num(v)}원`;

/** kv — 12 Medium 회색 라벨 + 13 Medium 값 */
const Kv = ({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) => (
  <span className={cn('flex items-center gap-1 whitespace-nowrap', className)}>
    <span className="text-xs leading-[18px] font-medium text-fg-tertiary">
      {label}
    </span>
    <span className="text-body-13-r font-medium text-fg-secondary">
      {value}
    </span>
  </span>
);

/** 실적_상세 (712:3774) — Type=처방 / 정산 한 줄 */
const DetailRow = ({
  kind,
  quantity,
  amount,
  resultLabel,
  result,
}: {
  kind: 'rx' | 'st';
  quantity: string;
  amount: string;
  resultLabel: string;
  result: string;
}) => (
  <div className="flex items-center justify-between gap-3">
    <div className="flex items-center gap-2">
      {kind === 'rx' ? (
        <Badge state="default">처방</Badge>
      ) : (
        <Badge state="success">정산</Badge>
      )}
      <Kv label="처방량" value={quantity} className="w-20" />
      <Kv label="금액" value={amount} />
    </div>
    <span className="flex items-center justify-end gap-1 whitespace-nowrap">
      <span className="text-xs leading-[18px] font-medium text-fg-tertiary">
        {resultLabel}
      </span>
      <span className="text-right text-body-14-sb text-fg-primary">
        {result}
      </span>
    </span>
  </div>
);

interface PrescriptionLineCardProps {
  line: PrescriptionCard;
  /** 약명으로 연 M-12 드로어에 「처방월 · 병의원」 처방량을 함께 보여 준다 (M-15 C) */
  clinicName?: string;
  /**
   * card: M-09 의약품 탭 (실적_의약품 712:4818 — 왼쪽 제약사 · 약가, 오른쪽 417 처방 · 정산)
   * stacked: M-18 처방 이미지 상세 오른쪽 (실적 처방이미지_의약품 789:8960 — 세로로 쌓음)
   * plain: M-17 제약사 표에서 펼친 행 안 (테두리 없음)
   */
  variant?: 'card' | 'stacked' | 'plain';
  /** 왼쪽 머리 (M-17 "7월 처방") */
  aside?: ReactNode;
}

/**
 * 처방 내역 카드 (M-09 의약품 탭 · M-17 제약사 탭 · M-18 처방 이미지 상세)
 * 금액은 서버 값을 그대로 쓴다. 정산 값은 같은 (처방월, 의약품) 정산 합이라 다른 카드에 반복될 수 있다.
 * 상태 배지(제약사 승인 · 흡수율 미달 · 미등록 거래처 · 거래 불가 · 정산 불가)와 비고는 아래 줄에 둔다.
 */
export const PrescriptionLineCard = ({
  line,
  clinicName,
  variant = 'card',
  aside,
}: PrescriptionLineCardProps) => {
  const badge = getLineBadge(line);
  const belowMin = isBelowMinimumEstimate(line);
  const stacked = variant === 'stacked';

  const meta = (
    <div className="flex min-w-0 flex-1 flex-col gap-1 text-body-13-r text-fg-secondary">
      <p className="truncate">
        {[
          line.pharmaCompanyName ?? '-',
          line.isReferenceDrug && '대조약',
          line.isBioequivalent && !line.isReferenceDrug && '생동',
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
      <p className="truncate">
        약가 {won(line.unitPrice)} · 수수료율{' '}
        {line.feeRate === null ? '-' : `${line.feeRate}%`}
      </p>
    </div>
  );

  const rows = (
    <div
      className={cn(
        'flex flex-col gap-2',
        stacked ? 'w-full' : 'w-full lg:w-[417px] lg:shrink-0',
      )}
    >
      <DetailRow
        kind="rx"
        quantity={num(line.outHospitalQuantity)}
        amount={won(line.outHospitalAmount)}
        resultLabel="예상 금액"
        result={belowMin ? '최소금액 기준 미달' : won(line.estimatedAmount)}
      />
      <DetailRow
        kind="st"
        quantity={num(line.settlementQuantity)}
        amount={won(line.settlementAmount)}
        resultLabel="확정 금액"
        result={won(line.confirmedAmount)}
      />
    </div>
  );

  const content = (
    <div className="flex min-w-0 flex-1 flex-col gap-3">
      <DrugNameLink
        drugCode={line.drugCode}
        context={
          clinicName
            ? {
                label: [
                  line.prescriptionYearMonth &&
                    formatYm(line.prescriptionYearMonth),
                  clinicName,
                ]
                  .filter(Boolean)
                  .join(' · '),
                quantity: line.outHospitalQuantity,
              }
            : undefined
        }
        className="self-start text-body-15-sb text-fg-primary"
      >
        {line.drugName ?? '—'}
      </DrugNameLink>
      <div
        className={cn(
          'flex gap-4',
          stacked ? 'flex-col' : 'flex-col lg:flex-row lg:items-start lg:gap-0',
        )}
      >
        {meta}
        {rows}
      </div>
      {(badge || line.remarks) && (
        <>
          <div aria-hidden className="h-px bg-line-grey" />
          <div className="flex flex-wrap items-center gap-2">
            {badge && (
              <Badge state={badge.state} emphasis="strong">
                {badge.label}
              </Badge>
            )}
            {line.remarks && (
              <p className="text-body-13-r text-fg-tertiary">{line.remarks}</p>
            )}
          </div>
        </>
      )}
    </div>
  );

  return (
    <article
      className={cn(
        'flex min-w-0 gap-6',
        variant !== 'plain' && 'rounded-12 border border-line-grey p-4',
      )}
    >
      {aside}
      {content}
    </article>
  );
};
